import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { useTheme } from '../../context/ThemeContext';
import { canAccessTeacherClassroom, canAccessStudentClassroom } from '../../utils/eduRoles';
import { useFieldBookPilotAccess } from '../../hooks/useFieldBookPilotAccess';
import ToolsNavMenu from './ToolsNavMenu';

const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, loading } = useAuth();
  const { t, language } = useTranslation();
  const { isDark, toggleTheme } = useTheme();
  const showTeacherClassroom = canAccessTeacherClassroom(user, { loading });
  const showStudentClassroom = canAccessStudentClassroom(user, { loading });
  const { isLoggedIn: showFieldBooks } = useFieldBookPilotAccess();

  const Loader = () => (
    <div className="px-4 py-2 flex items-center gap-2">
      <span className="loader w-4 h-4 border-2 border-gray-300 dark:border-gray-600 border-t-black dark:border-t-white rounded-full animate-spin"></span>
      <span className="text-neutral-400 dark:text-gray-300 text-base font-medium font-['Manrope']">{t.loading}</span>
    </div>
  );

  return (
    <>
      {/* Desktop Header */}
      <div className="hidden md:flex w-full px-6 py-3 bg-white dark:bg-zinc-950 border-b border-gray-200 dark:border-zinc-800 transition-colors duration-300 items-center justify-between">
        <div className="flex-1 flex justify-start items-center gap-2.5">
          <Link to="/" className="flex justify-start items-center gap-2.5">
            <img src={isDark ? "/icons/homepage_white_logo.svg" : "/images/logo.png"} alt="GeoSolver Logo" className="w-10 h-10" />
            <div className="justify-start text-black dark:text-white text-xl font-bold font-['Manrope']">GeoSolver</div>
            <div className="px-3 py-1 bg-gray-200 dark:bg-zinc-900 rounded flex justify-center items-center gap-2.5">
              <div className="justify-start text-black dark:text-white text-xs font-bold font-['Manrope']">{t.beta}</div>
            </div>
          </Link>
        </div>
        <div className="flex justify-start items-center gap-2">
          <Link
            to="/"
            className={`px-3 py-1 rounded-lg flex justify-center items-center gap-2.5 ${location.pathname === '/' ? 'text-black dark:text-white' : 'text-neutral-400 dark:text-zinc-400'} text-base font-medium font-['Manrope'] hover:text-black dark:hover:text-white`}
          >
            {t.home}
          </Link>
          <ToolsNavMenu t={t} language={language} user={user} showFieldBooks={showFieldBooks} />
          <Link
            to="/prices"
            className={`px-3 py-1 rounded-lg flex justify-center items-center gap-2.5 ${location.pathname === '/prices' ? 'text-black dark:text-white' : 'text-neutral-400 dark:text-zinc-400'} text-base font-medium font-['Manrope'] hover:text-black dark:hover:text-white`}
          >
            {t.prices}
          </Link>
          <Link
            to="/about"
            className={`px-3 py-1 rounded-lg flex justify-center items-center gap-2.5 ${location.pathname === '/about' ? 'text-black dark:text-white' : 'text-neutral-400 dark:text-zinc-400'} text-base font-medium font-['Manrope'] hover:text-black dark:hover:text-white`}
          >
            {t.about}
          </Link>
          <Link
            to="/contacts"
            className={`px-3 py-1 rounded-lg flex justify-center items-center gap-2.5 ${location.pathname === '/contacts' ? 'text-black dark:text-white' : 'text-neutral-400 dark:text-zinc-400'} text-base font-medium font-['Manrope'] hover:text-black dark:hover:text-white`}
          >
            {t.contacts}
          </Link>
          <Link
            to="/for-teachers"
            className={`px-3 py-1 rounded-lg flex justify-center items-center gap-2.5 ${location.pathname === '/for-teachers' ? 'text-black dark:text-white' : 'text-neutral-400 dark:text-zinc-400'} text-base font-medium font-['Manrope'] hover:text-black dark:hover:text-white`}
          >
            {t.forTeachers}
          </Link>
          <Link
            to="/gai"
            className={`px-3 py-1 rounded-lg flex justify-center items-center gap-2.5 ${location.pathname === '/gai' ? 'text-black dark:text-white' : 'text-neutral-400 dark:text-zinc-400'} text-base font-medium font-['Manrope'] hover:text-black dark:hover:text-white`}
          >
            {t.gai}
          </Link>
        </div>
        <div className="flex-1 flex justify-end items-center gap-3">
          {showTeacherClassroom && (
            <Link
              to="/classroom/dashboard"
              className="px-4 py-2 bg-black dark:bg-white rounded-lg flex justify-start items-center gap-3 hover:opacity-90 transition-opacity"
            >
              <img
                src={isDark ? '/icons/homepage_login_icon.svg' : '/icons/login_icon.svg'}
                alt="Classroom Icon"
                className="w-5 h-5 shrink-0"
              />
              <div className="justify-start text-white dark:text-black text-base font-medium font-['Manrope']">
                {language === 'bg' ? 'Класна стая' : 'Classroom'}
              </div>
            </Link>
          )}
          {showStudentClassroom && (
            <Link
              to="/classroom/assignments"
              className="px-4 py-2 bg-black dark:bg-white rounded-lg flex justify-start items-center gap-3 hover:opacity-90 transition-opacity"
            >
              <div className="justify-start text-white dark:text-black text-base font-medium font-['Manrope']">
                {language === 'bg' ? 'Задания' : 'Assignments'}
              </div>
            </Link>
          )}
          <Link to="/scientific-calculator" className="w-9 h-9 rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 dark:outline-zinc-700 flex justify-center items-center gap-3 hover:bg-gray-100 dark:hover:bg-zinc-900 transition-colors">
            <img src={isDark ? "/icons/homepage_calc_icon.svg" : "/icons/calc_icon.svg"} alt="Calculator Icon" className="w-6 h-6" />
          </Link>
          <button
            onClick={toggleTheme}
            className="w-9 h-9 rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 dark:outline-zinc-700 flex justify-center items-center gap-3 hover:bg-gray-100 dark:hover:bg-zinc-900 transition-colors"
          >
            <img src={isDark ? "/icons/homepage_night_icon.svg" : "/icons/night_mode_icon.svg"} alt="Theme Toggle" className="w-6 h-6" />
          </button>
          {loading ? (
            <Loader />
          ) : user ? (
            <Link to="/account" className="px-4 py-2 bg-black dark:bg-white rounded-lg flex justify-start items-center gap-3">
              <div className="justify-start text-white dark:text-black text-base font-medium font-['Manrope']">{t.account}</div>
              <img src={isDark ? "/icons/homepage_login_icon.svg" : "/icons/login_icon.svg"} alt="Account Icon" className="w-5 h-5" />
            </Link>
          ) : (
            <Link to="/login" className="px-4 py-2 bg-black dark:bg-white rounded-lg flex justify-start items-center gap-3">
              <div className="justify-start text-white dark:text-black text-base font-medium font-['Manrope']">{t.login}</div>
              <img src={isDark ? "/icons/homepage_login_icon.svg" : "/icons/login_icon.svg"} alt="Login Icon" className="w-5 h-5" />
            </Link>
          )}
        </div>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden w-full px-4 pt-4 pb-2 bg-white dark:bg-zinc-950 border-b border-gray-200 dark:border-zinc-800 transition-colors duration-300 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2">
          <img src={isDark ? "/icons/homepage_white_logo.svg" : "/images/logo.png"} alt="GeoSolver Logo" className="w-9 h-9" />
          <div className="text-black dark:text-white text-base font-bold font-['Manrope']">GeoSolver</div>
          <div className="px-3 py-1 bg-gray-200 dark:bg-zinc-900 rounded flex items-center gap-2.5">
            <div className="text-black dark:text-white text-xs font-bold font-['Manrope']">{t.beta}</div>
          </div>
        </Link>
        <button
          className="w-9 h-9 rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 dark:outline-zinc-700 flex items-center justify-center"
          onClick={() => setMobileMenuOpen(true)}
        >
          <img src="/icons/header_icon.svg" alt="Menu" className="w-6 h-6" />
        </button>
      </div>

      {/* Mobile Navigation Drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex justify-center items-start pt-16">
          <div className="w-96 px-4 pt-12 pb-6 bg-white dark:bg-zinc-950 border-b border-gray-200 dark:border-zinc-800 transition-colors duration-300 inline-flex flex-col justify-start items-start gap-6 rounded-lg shadow-lg">
            <div className="self-stretch inline-flex justify-between items-start">
              <div className="flex justify-start items-center gap-2">
                <img src={isDark ? "/icons/homepage_white_logo.svg" : "/images/logo.png"} alt="GeoSolver Logo" className="w-9 h-9" />
                <div className="justify-start text-black dark:text-white text-base font-bold font-['Manrope']">GeoSolver</div>
                <div className="px-3 py-1 bg-gray-200 dark:bg-zinc-900 rounded flex justify-center items-center gap-2.5">
                  <div className="justify-start text-black dark:text-white text-xs font-bold font-['Manrope']">{t.beta}</div>
                </div>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="w-9 h-9 rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 dark:outline-zinc-700 flex justify-center items-center">
                <img src="/icons/close_button.svg" alt="Close" className="w-6 h-6" />
              </button>
            </div>
            <div className="self-stretch flex flex-col justify-center items-start gap-2">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="self-stretch p-3 rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 dark:outline-zinc-700 inline-flex justify-start items-center gap-3">
                <div className="justify-start text-black dark:text-white text-sm font-medium font-['Manrope']">{t.home}</div>
                <img src="/icons/small_header_icon.svg" alt="Arrow" className="w-3 h-3" />
              </Link>
              <ToolsNavMenu
                t={t}
                language={language}
                user={user}
                showFieldBooks={showFieldBooks}
                variant="mobile"
                onNavigate={() => setMobileMenuOpen(false)}
              />
              <Link to="/prices" onClick={() => setMobileMenuOpen(false)} className="self-stretch p-3 rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 dark:outline-zinc-700 inline-flex justify-start items-center gap-3">
                <div className="justify-start text-black dark:text-white text-sm font-medium font-['Manrope']">{t.prices}</div>
                <img src="/icons/small_header_icon.svg" alt="Arrow" className="w-3 h-3" />
              </Link>
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="self-stretch p-3 rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 dark:outline-zinc-700 inline-flex justify-start items-center gap-3">
                <div className="justify-start text-black dark:text-white text-sm font-medium font-['Manrope']">{t.about}</div>
                <img src="/icons/small_header_icon.svg" alt="Arrow" className="w-3 h-3" />
              </Link>
              <Link to="/contacts" onClick={() => setMobileMenuOpen(false)} className="self-stretch p-3 rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 dark:outline-zinc-700 inline-flex justify-start items-center gap-3">
                <div className="justify-start text-black dark:text-white text-sm font-medium font-['Manrope']">{t.contacts}</div>
                <img src="/icons/small_header_icon.svg" alt="Arrow" className="w-3 h-3" />
              </Link>
              <Link to="/for-teachers" onClick={() => setMobileMenuOpen(false)} className="self-stretch p-3 rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 dark:outline-zinc-700 inline-flex justify-start items-center gap-3">
                <div className="justify-start text-black dark:text-white text-sm font-medium font-['Manrope']">{t.forTeachers}</div>
                <img src="/icons/small_header_icon.svg" alt="Arrow" className="w-3 h-3" />
              </Link>
              <Link to="/gai" onClick={() => setMobileMenuOpen(false)} className="self-stretch p-3 rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 dark:outline-zinc-700 inline-flex justify-start items-center gap-3">
                <div className="justify-start text-black dark:text-white text-sm font-medium font-['Manrope']">{t.gai}</div>
                <img src="/icons/small_header_icon.svg" alt="Arrow" className="w-3 h-3" />
              </Link>
              {showTeacherClassroom && (
                <Link
                  to="/classroom/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="self-stretch px-4 py-2 bg-black dark:bg-white rounded-lg inline-flex justify-start items-center gap-3 hover:opacity-90 transition-opacity"
                >
                  <img
                    src={isDark ? '/icons/homepage_login_icon.svg' : '/icons/login_icon.svg'}
                    alt=""
                    className="w-4 h-4 shrink-0"
                  />
                  <div className="justify-start text-white dark:text-black text-sm font-medium font-['Manrope']">
                    {language === 'bg' ? 'Класна стая' : 'Classroom'}
                  </div>
                </Link>
              )}
              {showStudentClassroom && (
                <Link
                  to="/classroom/assignments"
                  onClick={() => setMobileMenuOpen(false)}
                  className="self-stretch px-4 py-2 bg-black dark:bg-white rounded-lg inline-flex justify-start items-center gap-3 hover:opacity-90 transition-opacity"
                >
                  <div className="justify-start text-white dark:text-black text-sm font-medium font-['Manrope']">
                    {language === 'bg' ? 'Задания' : 'Assignments'}
                  </div>
                </Link>
              )}
            </div>
            <div className="inline-flex justify-end items-center gap-3">
              {loading ? (
                <Loader />
              ) : user ? (
                <Link to="/account" onClick={() => setMobileMenuOpen(false)} className="px-4 py-2 bg-black dark:bg-white rounded-lg flex justify-start items-center gap-3">
                  <div className="justify-start text-white dark:text-black text-sm font-medium font-['Manrope']">{t.account}</div>
                  <img src={isDark ? "/icons/homepage_login_icon.svg" : "/icons/login_icon.svg"} alt="Account Icon" className="w-4 h-4" />
                </Link>
              ) : (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="justify-start text-white dark:text-black text-sm font-medium font-['Manrope'] px-4 py-2 bg-black dark:bg-white rounded-lg flex items-center gap-3">
                  {t.login}
                  <img src={isDark ? "/icons/homepage_login_icon.svg" : "/icons/login_icon.svg"} alt="Login" className="w-3 h-3" />
                </Link>
              )}
              <div className="w-9 self-stretch rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 dark:outline-zinc-700 flex justify-center items-center gap-3">
                <img src={isDark ? "/icons/homepage_calc_icon.svg" : "/icons/calc_icon.svg"} alt="Calculator" className="w-3.5 h-3.5" />
              </div>
              <button
                onClick={toggleTheme}
                className="w-9 self-stretch rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 dark:outline-zinc-700 flex justify-center items-center gap-3 hover:bg-gray-100 dark:hover:bg-zinc-900 transition-colors"
              >
                <img src={isDark ? "/icons/homepage_night_icon.svg" : "/icons/night_mode_icon.svg"} alt="Theme Toggle" className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
